import { useTaskCustomFields } from "@jugl-web/domain-resources/tasks/hooks/useTaskCustomFields";
import { useMemo } from "react";
import { useTaskFieldList } from "@jugl-web/domain-resources/tasks/hooks/useTaskFieldList";
import { useTaskLabels } from "@jugl-web/domain-resources/tasks/hooks/useTaskLabels";
import { TaskCustomFieldType } from "@jugl-web/rest-api/tasks/models/TaskCustomField";
import { SUPPORTED_WHATS_APP_VARIABLES } from "../consts";
import { WhatsappVariableDefinition } from "../types/WhatsappVariableDefinition";

export const useSupportedWhatsappVariables = (
  entityId: string,
  boardId?: string
) => {
  const { boardCustomFields } = useTaskCustomFields({
    entityId,
    boardId: boardId ?? null,
  });
  const { isFieldEnabled, getFieldById } = useTaskFieldList({
    entityId,
    boardId: boardId ?? null,
  });
  const { areLabelsSupported } = useTaskLabels({ entityId });
  const variables: WhatsappVariableDefinition[] = useMemo(() => {
    const VARIABLE_ID_MAP: Record<string, string> = {
      "Customer Name": "customer",
      "Task Name": "task_name",
      "Due Date": "due_date",
      Label: "label",
      Status: "status",
      Priority: "priority",
    };

    const mapField = (
      id: string,
      label: string,
      type: WhatsappVariableDefinition["type"],
      order = 0
    ) => ({
      id,
      label,
      type,
      order,
    });

    const getSupportedVariableFields = () =>
      SUPPORTED_WHATS_APP_VARIABLES.flatMap((variable) => {
        const id = VARIABLE_ID_MAP[variable];

        if (!id) {
          return [mapField(variable, variable, "default")];
        }

        const field = getFieldById(id);
        if (
          field &&
          isFieldEnabled(field.id) &&
          (field.id !== "label" || areLabelsSupported)
        ) {
          return [mapField(variable, variable, "default", field.order)];
        }

        return [];
      });

    const getCustomVariableFields = () => {
      const getPrefix = (type: TaskCustomFieldType) => {
        switch (type) {
          case "dropdown":
            return "dd_";
          case "date":
            return "dt_";
          default:
            return "";
        }
      };

      return boardCustomFields
        .map((field) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const taskField = getFieldById(field.id)!;
          return {
            customField: field,
            taskField,
          };
        })
        .filter(({ taskField }) => !!taskField?.visibility)
        .map(({ customField, taskField }) =>
          mapField(
            `Cf_${getPrefix(customField.type)}${customField.id}`,
            taskField.title,
            customField.type as WhatsappVariableDefinition["type"],
            taskField.order
          )
        );
    };

    return [...getSupportedVariableFields(), ...getCustomVariableFields()]
      .sort((a, b) => a.order - b.order)
      .map(({ id, label, type }) => ({ id, label, type }));
  }, [areLabelsSupported, boardCustomFields, getFieldById, isFieldEnabled]);

  return { variables };
};
